import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, ArrowRight, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { subscriptionAPI } from '../lib/api';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('polling'); // polling | success | failed | expired
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  const pollStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 8;
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }
    try {
      const res = await subscriptionAPI.status(sessionId);
      const data = res.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      }
      if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      // Keep polling
      setTimeout(() => pollStatus(attempts + 1), 2500);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollStatus(attempts + 1), 2500);
      } else {
        setStatus('failed');
      }
    }
  }, [sessionId]);

  useEffect(() => {
    pollStatus(0);
  }, [pollStatus]);

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="nx-card p-8 sm:p-10 text-center max-w-md w-full"
      >
        {status === 'polling' && (
          <>
            <Loader2 className="w-14 h-14 mx-auto mb-4 animate-spin" style={{ color: '#0D9488' }} />
            <h2 className="text-xl font-extrabold mb-2" style={{ color: 'var(--t-text-heading)' }}>
              Processing Payment...
            </h2>
            <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
              Please wait while we confirm your payment
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: 'rgba(56,161,105,0.12)' }}>
              <CheckCircle2 className="w-8 h-8" style={{ color: '#38A169' }} />
            </div>
            <h2 className="text-2xl font-extrabold mb-2" data-testid="payment-success-title" style={{ color: 'var(--t-text-heading)' }}>
              Payment Successful!
            </h2>
            <p className="text-sm mb-1" style={{ color: 'var(--t-text-muted)' }}>
              You've been upgraded to
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(13,148,136,0.2)' }}>
              <Crown className="w-4 h-4" style={{ color: '#0D9488' }} />
              <span className="font-bold text-sm" style={{ color: '#0D9488' }}>
                {paymentData?.plan_name || 'Premium'}
              </span>
            </div>
            <div className="space-y-3">
              <Button
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="w-full h-11 font-bold text-sm uppercase tracking-wider rounded-lg"
                style={{ background: '#0D9488', color: '#fff' }}
              >
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pricing')}
                className="w-full h-10 text-sm rounded-lg"
                style={{ borderColor: 'var(--t-border)', color: 'var(--t-text-muted)' }}
              >
                View Plans
              </Button>
            </div>
          </>
        )}

        {(status === 'failed' || status === 'expired') && (
          <>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: 'rgba(229,62,62,0.12)' }}>
              <XCircle className="w-8 h-8" style={{ color: '#E53E3E' }} />
            </div>
            <h2 className="text-2xl font-extrabold mb-2" data-testid="payment-failed-title" style={{ color: 'var(--t-text-heading)' }}>
              {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
            </h2>
            <p className="text-sm mb-6" style={{ color: 'var(--t-text-muted)' }}>
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : "We couldn't confirm your payment. Please try again or contact support."}
            </p>
            <Button
              data-testid="retry-payment-btn"
              onClick={() => navigate('/pricing')}
              className="w-full h-11 font-bold text-sm uppercase tracking-wider rounded-lg"
              style={{ background: '#0D9488', color: '#fff' }}
            >
              Try Again
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
